/**
 * Query keys.
 *
 * Every key begins with `inai-inventory`, and that prefix is not decoration. The module is
 * mounted into a host application that owns the `QueryClient` and already has keys of its
 * own — Accounts uses `['items']` for its own item list today. Without a namespace, the
 * module's `invalidateQueries` would blow away the host's cache and the host's would blow
 * away ours, intermittently, in a way that looks like a network flake.
 *
 * `inventoryKeys.all` is also the one call a host needs to drop everything this module has
 * cached — on sign-out, or on a tenant switch.
 */
const ROOT = 'inai-inventory';
export const inventoryKeys = {
    all: [ROOT],
    health: () => [ROOT, 'health'],
    organization: () => [ROOT, 'organization'],
    entitlements: () => [ROOT, 'entitlements'],
    items: {
        all: () => [ROOT, 'items'],
        list: (filter = {}) => [ROOT, 'items', 'list', filter],
        detail: (itemId) => [ROOT, 'items', 'detail', itemId],
        byCode: (code) => [ROOT, 'items', 'by-code', code],
        variants: (itemId) => [ROOT, 'items', 'variants', itemId],
        barcodes: (itemId) => [ROOT, 'items', 'barcodes', itemId],
    },
    itemGroups: {
        all: () => [ROOT, 'item-groups'],
        list: () => [ROOT, 'item-groups', 'list'],
    },
    barcodes: {
        all: () => [ROOT, 'barcodes'],
        resolve: (barcode) => [ROOT, 'barcodes', 'resolve', barcode],
    },
    uoms: {
        all: () => [ROOT, 'uoms'],
        list: () => [ROOT, 'uoms', 'list'],
        conversions: (itemId) => [ROOT, 'uoms', 'conversions', itemId ?? null],
        convert: (params) => [ROOT, 'uoms', 'convert', params],
    },
    locations: {
        all: () => [ROOT, 'locations'],
        warehouses: () => [ROOT, 'locations', 'warehouses'],
        warehouse: (warehouseId) => [ROOT, 'locations', 'warehouses', 'detail', warehouseId],
        warehouseCount: () => [ROOT, 'locations', 'warehouses', 'count'],
        zones: (warehouseId) => [ROOT, 'locations', 'zones', warehouseId],
        bins: (zoneId) => [ROOT, 'locations', 'bins', zoneId],
    },
    stock: {
        all: () => [ROOT, 'stock'],
        movements: (filter = {}) => [ROOT, 'stock', 'movements', filter],
        movement: (movementId) => [ROOT, 'stock', 'movement', movementId],
        balances: (filter = {}) => [ROOT, 'stock', 'balances', filter],
        balance: (params) => [ROOT, 'stock', 'balance', params],
        availability: (params) => [ROOT, 'stock', 'availability', params],
    },
    reservations: {
        all: () => [ROOT, 'reservations'],
        list: (filter = {}) => [ROOT, 'reservations', 'list', filter],
        detail: (reservationId) => [ROOT, 'reservations', 'detail', reservationId],
    },
    counting: {
        all: () => [ROOT, 'counting'],
        list: (filter = {}) => [ROOT, 'counting', 'list', filter],
        detail: (sessionId) => [ROOT, 'counting', 'detail', sessionId],
        summary: (sessionId) => [ROOT, 'counting', 'summary', sessionId],
        manifest: (sessionId) => [ROOT, 'counting', 'manifest', sessionId],
    },
    replenishment: {
        all: () => [ROOT, 'replenishment'],
        rules: (warehouseId) => [ROOT, 'replenishment', 'rules', warehouseId ?? null],
        rule: (params) => [ROOT, 'replenishment', 'rule', params],
        runs: (warehouseId) => [ROOT, 'replenishment', 'runs', warehouseId ?? null],
        run: (runId) => [ROOT, 'replenishment', 'run', runId],
        suggestions: (filter = {}) => [ROOT, 'replenishment', 'suggestions', filter],
    },
};
//# sourceMappingURL=keys.js.map